'use client';

import type {
  HelloBars,
  SystemListenerProps,
  UserListenerProps,
} from '@/types';

import React from 'react';
import { Slide, ToastContainer } from 'react-toastify';
import { cookieStorageManagerSSR, useDisclosure } from '@chakra-ui/react';
import { css, Global } from '@emotion/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { io, type Socket } from 'socket.io-client';
import { useShallow } from 'zustand/react/shallow';

import '@/i18n';

import { ChakraProvider, HelloBar } from '@/components';
import useIsAdmin from '@/hooks/store/useIsAdmin';
import { theme } from '@/theme';
import { api, createQueryClient, setDayjsJalaliCalendar } from '@/utils';

const WelcomeModal = dynamic(() => import('@/components/ui/WelcomeModal'), {
  ssr: false,
});

const EmploymentInformationModal = dynamic(
  () => import('@/components/ui/EmploymentInformationModal'),
  {
    ssr: false,
  }
);

const MarketingInformationModal = dynamic(
  () => import('@/components/ui/MarketingInformationModal'),
  {
    ssr: false,
  }
);

const Julia = dynamic(() => import('@/components/ui/Julia'), {
  ssr: false,
});
const subscribeToHydration = (): (() => void) => () => undefined;

const Provider: React.FC<{
  token: string | undefined;
  children: React.ReactNode;
  helloBar: HelloBars | null;
}> = ({ children, token, helloBar }) => {
  const pathname = usePathname();

  const hasHelloBar =
    !pathname.includes('identity') && !pathname.includes('auth');

  setDayjsJalaliCalendar();

  api.interceptors.request.use((config) => {
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  });
  const [isOpen, setIsOpen] = React.useState(true);
  const [isOpenMarketingModal, setIsOpenMarketingModal] = React.useState(true);

  const onClose = () => {
    setIsOpen(false);
  };
  const [queryClient] = React.useState(() => createQueryClient({}));

  const isHydrated = React.useSyncExternalStore(
    subscribeToHydration,
    () => true,
    () => false
  );
  const isAdminUser = useIsAdmin(useShallow((state) => state.isAdmin));
  const availableCustomer = isHydrated && token && !isAdminUser;
  const colors = theme.colors as Record<string, Record<number, string>>;
  const radii = theme.radii as Record<string, string>;
  const {
    isOpen: eventIsOpen,
    onOpen,
    onClose: eventOnClose,
  } = useDisclosure();
  const [event, setEvent] = React.useState<{
    title: string;
    payload: string;
    icon: string;
    link: string;
    message_category: string;
  } | null>(null);

  const [socket, setSocket] = React.useState<Socket | null>(null);
  React.useEffect((): any => {
    if (!socket && process.env.NEXT_PUBLIC_LIATEAM_SOCKET) {
      setSocket(() =>
        io(process.env.NEXT_PUBLIC_LIATEAM_SOCKET || '', {
          extraHeaders: {
            Authorization: token || '',
            realm: 'shop',
          },
          forceNew: true,
          reconnectionAttempts: 3,
          timeout: 2000,
        })
      );
    }
    return () => socket?.close();
  }, []);

  React.useEffect(() => {
    if (socket === null) {
      return;
    }
    if (socket) {
      socket.on('USER', (userEvent: UserListenerProps) => {
        setEvent(userEvent);
        onOpen();
      });

      socket.on('SYSTEM', (newEvent: SystemListenerProps) => {
        if (newEvent.command === 'REFRESH') {
          window.location.reload();
        }
      });
    }
    if (!token) {
      socket.disconnect();
    }
  }, [socket]);

  return (
    <QueryClientProvider client={queryClient}>
      <ChakraProvider
        theme={theme}
        cssVarsRoot="body"
        colorModeManager={cookieStorageManagerSSR('chakra-ui-color-mode')}
      >
        {helloBar?.id && hasHelloBar && <HelloBar data={helloBar} />}
        {availableCustomer && (
          <WelcomeModal isOpen={isOpen} onClose={onClose} />
        )}
        {availableCustomer && !pathname.includes('auth') && (
          <MarketingInformationModal
            isOpen={isOpenMarketingModal}
            onClose={setIsOpenMarketingModal}
          />
        )}
        {availableCustomer && !pathname.includes('auth') && (
          <EmploymentInformationModal />
        )}
        {isHydrated && token && (
          <Julia isOpen={eventIsOpen} onClose={eventOnClose} event={event} />
        )}
        <Global
          styles={css`
            .Toastify__toast {
              background-color: ${colors?.gray?.[700]};
              color: ${colors?.gray['100']};
              font-weight: bold !important;
              font-size: 16px !important;
              border-radius: ${radii.md} !important;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
              width: 340px;
              min-height: 50px !important;
              font-family: inherit;
              margin-bottom: 5px !important;
            }
            .Toastify__toast--success {
              background-color: ${colors.success[300]} !important;
              color: ${colors.gray['800']} !important;
              svg {
                fill: ${colors.success[500]};
              }
            }
            .Toastify__toast--warning {
              background-color: ${colors.warning[300]} !important;
              color: ${colors.gray['800']} !important;
              svg {
                fill: ${colors.warning[500]};
              }
            }
            .Toastify__toast--error {
              background-color: ${colors.error[300]} !important;
              color: ${colors.gray['800']} !important;
              svg {
                fill: ${colors.error[500]};
              }
            }
            .Toastify__spinner {
              border-color: ${colors.loading[500]} !important;
              border-right-color: ${colors.loading[300]} !important;
            }
            .Toastify__toast--info {
              background-color: ${colors.info[300]};
              color: ${colors.gray['800']} !important;
              svg {
                fill: ${colors.info[500]};
              }
            }
            @media (max-width: 768px) {
             .Toastify__toast {
               margin-bottom: 10px !important;
            }
          `}
        />
        <ToastContainer
          position="bottom-center"
          transition={Slide}
          autoClose={3000}
          hideProgressBar={true}
          newestOnTop
          closeOnClick={false}
          rtl={true}
          draggable
          closeButton={false}
          draggablePercent={20}
        />
        {children}
      </ChakraProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default Provider;
